/*                                                     */
import {
	BLOCK_EFFECT,
	BRANCH_EFFECT,
	CLEAN,
	DESTROYED,
	DIRTY,
	EFFECT,
	ASYNC,
	INERT,
	RENDER_EFFECT,
	ROOT_EFFECT,
	MAYBE_DIRTY
} from '#client/constants';
import { async_mode_flag } from '../../flags/index.js';
import { deferred, define_property } from '../../shared/utils.js';
import {
	active_effect,
	is_dirty,
	is_updating_effect,
	set_is_updating_effect,
	set_signal_status,
	update_effect
} from '../runtime.js';
import * as e from '../errors.js';
import { flush_tasks, has_pending_tasks, queue_micro_task } from '../dom/task.js';
import { DEV } from 'esm-env';
import { invoke_error_boundary } from '../error-handling.js';
import { old_values } from './sources.js';
import { unlink_effect } from './effects.js';

/*                     */
const batches = new Set();

/*                       */
export let current_batch = null;

/**
 *
 *
 *
 */
export let previous_batch = null;

/**
 *
 *
 *
 *
 */
export let batch_deriveds = null;

/*                          */
export let effect_pending_updates = new Set();

/*                   */
let queued_root_effects = [];

/*                        */
let last_scheduled_effect = null;

let is_flushing = false;
export let is_flushing_sync = false;

export class Batch {
	/**
 *
 *
 *
 */
	current = new Map();

	/**
 *
 *
 *
 */
	#previous = new Map();

	/**
 *
 *
 *
 */
	#callbacks = new Set();

	/**
 *
 */
	#pending = 0;

	/**
 *
 *
 *
 */
	#deferred = null;

	/**
 *
 *
 */
	#neutered = false;

	/**
 *
 *
 *
 *
 */
	#async_effects = [];

	/**
 *
 *
 *
 */
	#boundary_async_effects = [];

	/**
 *
 *
 *
 */
	#render_effects = [];

	/**
 *
 *
 */
	#effects = [];

	/**
 *
 *
 *
 */
	#block_effects = [];

	/**
 *
 *
 */
	#dirty_effects = [];

	/**
 *
 *
 */
	#maybe_dirty_effects = [];

	/**
 *
 *
 *
 */
	skipped_effects = new Set();

	/**
 *
 *
 */
	process(root_effects) {
		queued_root_effects = [];

		previous_batch = null;

		/*                                                         */
		var current_values = null;

		/*                                                        */
		/*                                                 */
		/*                         */
		if (async_mode_flag && batches.size > 1) {
			current_values = new Map();
			batch_deriveds = new Map();

			for (const [source, current] of this.current) {
				current_values.set(source, { v: source.v, wv: source.wv });
				source.v = current;
			}

			for (const batch of batches) {
				if (batch === this) continue;

				for (const [source, previous] of batch.#previous) {
					if (!current_values.has(source)) {
						current_values.set(source, { v: source.v, wv: source.wv });
						source.v = previous;
					}
				}
			}
		}

		for (const root of root_effects) {
			this.#traverse_effect_tree(root);
		}

		/*                                                       */
		/*                                           */
		if (this.#async_effects.length === 0 && this.#pending === 0) {
			this.#commit();

			var render_effects = this.#render_effects;
			var effects = this.#effects;

			this.#render_effects = [];
			this.#effects = [];
			this.#block_effects = [];

			/*                                                                                                               */
			/*                                                                                              */
			previous_batch = current_batch;
			current_batch = null;

			flush_queued_effects(render_effects);
			flush_queued_effects(effects);

			/*                                                                                                               */
			/*                                                                                                           */
			/*                                                                                  */
			if (current_batch === null) {
				current_batch = this;
			} else {
				batches.delete(this);
			}

			this.#deferred?.resolve();
		} else {
			this.#defer_effects(this.#render_effects);
			this.#defer_effects(this.#effects);
			this.#defer_effects(this.#block_effects);
		}

		if (current_values) {
			for (const [source, { v, wv }] of current_values) {
				/*                                            */
				/*                                                   */
				if (source.wv <= wv) {
					source.v = v;
				}
			}

			batch_deriveds = null;
		}

		for (const effect of this.#async_effects) {
			update_effect(effect);
		}

		for (const effect of this.#boundary_async_effects) {
			update_effect(effect);
		}

		this.#async_effects = [];
		this.#boundary_async_effects = [];
	}

	/**
 *
 *
 *
 */
	#traverse_effect_tree(root) {
		root.f ^= CLEAN;

		var effect = root.first;

		while (effect !== null) {
			var flags = effect.f;
			var is_branch = (flags & (BRANCH_EFFECT | ROOT_EFFECT)) !== 0;
			var is_skippable_branch = is_branch && (flags & CLEAN) !== 0;

			var skip = is_skippable_branch || (flags & INERT) !== 0 || this.skipped_effects.has(effect);

			if (!skip && effect.fn !== null) {
				if (is_branch) {
					effect.f ^= CLEAN;
				} else if ((flags & EFFECT) !== 0) {
					this.#effects.push(effect);
				} else if (async_mode_flag && (flags & RENDER_EFFECT) !== 0) {
					this.#render_effects.push(effect);
				} else if ((flags & CLEAN) === 0) {
					if ((flags & ASYNC) !== 0) {
						var effects = effect.b?.is_pending()
							? this.#boundary_async_effects
							: this.#async_effects;

						effects.push(effect);
					} else if (is_dirty(effect)) {
						if ((effect.f & BLOCK_EFFECT) !== 0) this.#block_effects.push(effect);
						update_effect(effect);
					}
				}

				var child = effect.first;

				if (child !== null) {
					effect = child;
					continue;
				}
			}

			var parent = effect.parent;
			effect = effect.next;

			while (effect === null && parent !== null) {
				effect = parent.next;
				parent = parent.parent;
			}
		}
	}

	/**
 *
 */
	#defer_effects(effects) {
		for (const e of effects) {
			const target = (e.f & DIRTY) !== 0 ? this.#dirty_effects : this.#maybe_dirty_effects;
			target.push(e);

			/*                                                                        */
			set_signal_status(e, CLEAN);
		}

		effects.length = 0;
	}

	/**
 *
 *
 *
 *
 */
	capture(source, value) {
		if (!this.#previous.has(source)) {
			this.#previous.set(source, value);
		}

		this.current.set(source, source.v);
	}

	activate() {
		current_batch = this;
	}

	deactivate() {
		current_batch = null;
		previous_batch = null;

		for (const update of effect_pending_updates) {
			effect_pending_updates.delete(update);
			update();

			if (current_batch !== null) {
				/*                    */
				break;
			}
		}
	}

	neuter() {
		this.#neutered = true;
	}

	flush() {
		if (queued_root_effects.length > 0) {
			flush_effects();
		} else {
			this.#commit();
		}

		if (current_batch !== this) {
			/*                                                                  */
			/*                                                              */
			return;
		}

		if (this.#pending === 0) {
			batches.delete(this);
		}

		this.deactivate();
	}

	/**
 *
 */
	#commit() {
		if (!this.#neutered) {
			for (const fn of this.#callbacks) {
				fn();
			}
		}

		this.#callbacks.clear();
	}

	increment() {
		this.#pending += 1;
	}

	decrement() {
		this.#pending -= 1;

		if (this.#pending === 0) {
			for (const e of this.#dirty_effects) {
				set_signal_status(e, DIRTY);
				schedule_effect(e);
			}

			for (const e of this.#maybe_dirty_effects) {
				set_signal_status(e, MAYBE_DIRTY);
				schedule_effect(e);
			}

			this.#render_effects = [];
			this.#effects = [];

			this.flush();
		} else {
			this.deactivate();
		}
	}

	/*                         */
	add_callback(fn) {
		this.#callbacks.add(fn);
	}

	settled() {
		return (this.#deferred ??= deferred()).promise;
	}

	static ensure() {
		if (current_batch === null) {
			const batch = (current_batch = new Batch());
			batches.add(current_batch);

			if (!is_flushing_sync) {
				Batch.enqueue(() => {
					if (current_batch !== batch) {
						/*                                   */
						return;
					}

					batch.flush();
				});
			}
		}

		return current_batch;
	}

	/*                           */
	static enqueue(task) {
		queue_micro_task(task);
	}
}

/**
 *
 *
 *
 *
 *
 */
export function flushSync(fn) {
	if (async_mode_flag && active_effect !== null) {
		/*                                                                                                               */
		e.flush_sync_in_effect();
	}

	var was_flushing_sync = is_flushing_sync;
	is_flushing_sync = true;

	try {
		var result;

		if (fn) {
			flush_effects();
			result = fn();
		}

		while (true) {
			flush_tasks();

			if (queued_root_effects.length === 0 && !has_pending_tasks()) {
				current_batch?.flush();

				/*                                                                     */
				if (queued_root_effects.length === 0) {
					/*                                                                              */
					/*                                                                                    */
					last_scheduled_effect = null;

					return /*            */ (result);
				}
			}

			flush_effects();
		}
	} finally {
		is_flushing_sync = was_flushing_sync;
	}
}

function flush_effects() {
	var was_updating_effect = is_updating_effect;
	is_flushing = true;

	try {
		var flush_count = 0;
		set_is_updating_effect(true);

		while (queued_root_effects.length > 0) {
			var batch = Batch.ensure();

			if (flush_count++ > 1000) {
				if (DEV) {
					var updates = new Map();

					for (const source of batch.current.keys()) {
						for (const [stack, update] of source.updated ?? []) {
							var entry = updates.get(stack);

							if (!entry) {
								entry = { error: update.error, count: 0 };
								updates.set(stack, entry);
							}

							entry.count += update.count;
						}
					}

					for (const update of updates.values()) {
						/*                                  */
						console.error(update.error);
					}
				}

				infinite_loop_guard();
			}

			batch.process(queued_root_effects);
			old_values.clear();
		}
	} finally {
		is_flushing = false;
		set_is_updating_effect(was_updating_effect);

		last_scheduled_effect = null;
	}
}

function infinite_loop_guard() {
	try {
		e.effect_update_depth_exceeded();
	} catch (error) {
		if (DEV) {
			/*                                               */
			define_property(error, 'stack', { value: '' });
		}

		/*                                                       */
		/*                                                      */
		invoke_error_boundary(error, last_scheduled_effect);
	}
}

/*                          */
export let eager_block_effects = null;

/**
 *
 *
 */
function flush_queued_effects(effects) {
	var length = effects.length;
	if (length === 0) return;

	var i = 0;

	while (i < length) {
		var effect = effects[i++];

		if ((effect.f & (DESTROYED | INERT)) === 0 && is_dirty(effect)) {
			eager_block_effects = [];

			update_effect(effect);

			/*                                                                            */
			/*                                                                        */
			/*                                                                          */
			/*                                                                   */
			/*                 */
			if (effect.deps === null && effect.first === null && effect.nodes_start === null) {
				/*                                                              */
				/*                        */
				if (effect.teardown === null && effect.ac === null) {
					/*                                */
					unlink_effect(effect);
				} else {
					/*                                                    */
					effect.fn = null;
				}
			}

			/*                                                                                              */
			/*                                                                        */
			if (eager_block_effects?.length > 0) {
				/*                                                   */
				old_values.clear();

				for (const e of eager_block_effects) {
					update_effect(e);
				}

				eager_block_effects = [];
			}
		}
	}

	eager_block_effects = null;
}

/**
 *
 *
 */
export function schedule_effect(signal) {
	var effect = (last_scheduled_effect = signal);

	while (effect.parent !== null) {
		effect = effect.parent;
		var flags = effect.f;

		/*                                                                       */
		/*                                                                 */
		if (is_flushing && effect === active_effect && (flags & BLOCK_EFFECT) !== 0) {
			return;
		}

		if ((flags & (ROOT_EFFECT | BRANCH_EFFECT)) !== 0) {
			if ((flags & CLEAN) === 0) return;
			effect.f ^= CLEAN;
		}
	}

	queued_root_effects.push(effect);
}

/**
 *
 */
export function clear() {
	batches.clear();
}
